/**
 * A digitisation in progress, kept in this browser until it is sent (Rx-12): the doctor, the
 * medicine lines, the validity and the notes for one upload. Leaving the upload's page — to check
 * another, or by accident — loses nothing; the page offers it back. A draft is dropped once the
 * upload is digitised or rejected, when it is emptied, or after a week.
 *
 * Storage can be missing or refuse (a private window, blocked site data): then nothing is kept and
 * nothing breaks.
 */

import type { DoctorOption } from "../api/pharmacist";
import type { RxLineDraft } from "./rxLines";

export interface DigitiseDraft {
  doctor: DoctorOption | null;
  lines: RxLineDraft[];
  validDays: string;
  notes: string;
}

export interface SavedDraft extends DigitiseDraft {
  savedAt: number;
}

const PREFIX = "dp.digitise-draft.";
const KEEP_MS = 7 * 24 * 60 * 60_000;

/** This browser's storage, or undefined where it can't be used. */
export function draftStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export const isEmptyDraft = (d: DigitiseDraft): boolean =>
  !d.doctor && d.lines.length === 0 && !d.notes.trim() && !d.validDays.trim();

const isLine = (l: unknown): l is RxLineDraft =>
  typeof l === "object" &&
  l !== null &&
  typeof (l as RxLineDraft).medicine_id === "string" &&
  typeof (l as RxLineDraft).medicine_name === "string" &&
  Array.isArray((l as RxLineDraft).time_of_day);

/** The upload's draft, if one was kept and is less than a week old. */
export function loadDraft(storage: Storage | undefined, uploadId: string, now = Date.now()): SavedDraft | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(PREFIX + uploadId);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<SavedDraft>;
    if (typeof d.savedAt !== "number" || now - d.savedAt > KEEP_MS) {
      storage.removeItem(PREFIX + uploadId);
      return null;
    }
    const draft: SavedDraft = {
      savedAt: d.savedAt,
      doctor: d.doctor && typeof d.doctor.id === "string" ? d.doctor : null,
      lines: Array.isArray(d.lines) ? d.lines.filter(isLine) : [],
      validDays: typeof d.validDays === "string" ? d.validDays : "",
      notes: typeof d.notes === "string" ? d.notes : "",
    };
    return isEmptyDraft(draft) ? null : draft;
  } catch {
    return null;
  }
}

/** Keep the draft (an empty one is dropped instead). */
export function saveDraft(storage: Storage | undefined, uploadId: string, draft: DigitiseDraft, now = Date.now()): void {
  if (!storage) return;
  try {
    if (isEmptyDraft(draft)) storage.removeItem(PREFIX + uploadId);
    else storage.setItem(PREFIX + uploadId, JSON.stringify({ ...draft, savedAt: now }));
  } catch {
    /* full or blocked: the draft just isn't kept */
  }
}

export function clearDraft(storage: Storage | undefined, uploadId: string): void {
  try {
    storage?.removeItem(PREFIX + uploadId);
  } catch {
    /* blocked */
  }
}
